import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  Calendar as CalendarIcon,
  Users,
  User as UserIcon,
  LogOut,
  Menu,
  X,
  Droplet,
  Command as CommandIcon,
  Settings as SettingsIcon,
  Package,
  Truck,
  Import,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { cn, initials } from '../lib/utils';
import CommandPalette from './CommandPalette';
import NextJobFloat from './NextJobFloat';
import { LogoMark, LogoLockup } from './Logo';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, adminOnly: false },
  { to: '/jobs', label: 'Jobs', icon: Briefcase, end: false, adminOnly: false },
  { to: '/calendar', label: 'Calendar', icon: CalendarIcon, end: false, adminOnly: false },
  { to: '/parts-orders', label: 'Parts Orders', icon: Truck, end: false, adminOnly: false },
  { to: '/clients', label: 'Clients', icon: Users, end: false, adminOnly: false },
  { to: '/consumables', label: 'Consumables', icon: Package, end: false, adminOnly: false },
  { to: '/timetree', label: 'TimeTree Import', icon: Import, end: false, adminOnly: true },
  { to: '/settings', label: 'Xero & Settings', icon: SettingsIcon, end: false, adminOnly: true },
  { to: '/profile', label: 'Profile', icon: UserIcon, end: false, adminOnly: false },
];

export default function AppShell() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const navItems = NAV.filter((item) => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-surface-soft">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 flex-col z-30">
        <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => {}} navItems={navItems} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col shadow-pop animate-in slide-in-from-left">
            <SidebarContent
              user={user}
              onLogout={handleLogout}
              onNavigate={() => setMobileOpen(false)}
              navItems={navItems}
              showClose
              onClose={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* Mobile top bar — respects iPhone notch via safe-area-inset-top */}
      <header
        className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center px-4 gap-3"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'max(1rem, env(safe-area-inset-left))',
          paddingRight: 'max(1rem, env(safe-area-inset-right))',
          minHeight: 'calc(3.5rem + env(safe-area-inset-top))',
        }}
      >
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <LogoLockup size={30} compact />
        </div>
        <div className="ml-auto">
          <button
            onClick={() => navigate('/profile')}
            className="w-8 h-8 rounded-full bg-brand-100 grid place-items-center text-brand-700 font-bold text-sm"
          >
            {initials(user?.full_name || user?.username)}
          </button>
        </div>
      </header>

      {/* Main content — respects iPhone home-indicator via safe-area-inset-bottom */}
      <main className="lg:pl-64">
        <div
          className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 max-w-[1600px] mx-auto"
          style={{
            paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            paddingLeft: 'max(1rem, env(safe-area-inset-left))',
            paddingRight: 'max(1rem, env(safe-area-inset-right))',
          }}
        >
          <Outlet />
        </div>
      </main>

      <CommandPalette />
      <NextJobFloat />
    </div>
  );
}

function SidebarContent({
  user,
  onLogout,
  onNavigate,
  navItems,
  showClose,
  onClose,
}: {
  user: any;
  onLogout: () => void;
  onNavigate: () => void;
  navItems: typeof NAV;
  showClose?: boolean;
  onClose?: () => void;
}) {
  return (
    <>
      <div className="h-16 px-4 flex items-center justify-between border-b border-slate-100">
        <LogoLockup size={40} />
        {showClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.to.replace(/\//g, '') || 'dashboard'}-link`}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition',
                isActive
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}

        <div className="pt-3 mt-3 border-t border-slate-100">
          <button
            onClick={() => {
              const evt = new KeyboardEvent('keydown', {
                key: 'k',
                metaKey: true,
                bubbles: true,
              });
              window.dispatchEvent(evt);
              onNavigate();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition"
          >
            <CommandIcon className="h-4 w-4" />
            Quick actions
            <kbd className="ml-auto font-mono bg-slate-100 rounded px-1.5 py-0.5 text-[10px] text-slate-500">
              ⌘K
            </kbd>
          </button>
        </div>
      </nav>

      <div className="p-3 border-t border-slate-100">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-brand-100 grid place-items-center text-brand-700 font-bold text-sm shrink-0">
            {initials(user?.full_name || user?.username)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              {user?.full_name || user?.username}
            </div>
            <div className="text-xs text-slate-500 truncate">{user?.role}</div>
          </div>
          <button
            onClick={onLogout}
            className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition"
            title="Sign out"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}
